import { decodeBase64Attachment } from "@qdabra/infopath-attachments";
import { ATTACHMENT_TYPE_SHAREPOINT } from "../../constants/attachmentType.constants";
import { FILE_ATTACH_URL_KEY, FILE_DATA_KEY, IS_LOCAL_KEY } from "../../constants/file.constants";
import { initiateFileDownload } from "../../utility/download.helpers";
import { getFileProperties } from "../../utility/fileHelpers";
import { getNodeData, setNodeData } from "../../utility/nodeDataHelpers";
import { ownerDocument } from "../../xmlUtility";
import { DIALOG_RESULT_CANCEL, DIALOG_RESULT_RETRIEVED } from "../UI/dialog.constants";
import failureDialog from "../UI/failureDialog";
import showFileActionDialog, { ACTION_CANCEL, ACTION_DOWNLOAD, ACTION_REMOVE, ACTION_REPLACE } from "../UI/fileActionDialog";
import fileDialog from "../UI/fileDialog";
import { showLoaderText } from '../UI/uiLoader.stack.promise';


// NodeId is defined in dataSourceCollection.js

// ObjectModel is
// { 
//   getNodeById: (NodeId) -> DataSourceNode,
//   setNodeValueAsync: (DOMNode, String) -> Promise
// }

// "ControlType" is "FileAttachment" | "SharePointFileAttachment"

// (ObjectModel, NodeId, ControlType) -> AttachmentClickHandler
function attachmentClickHandler(om, nodeId, controlType, disableEditing) {
    // DataSourceNode
    const field = om.getNodeById(nodeId);

    //Boolean
    const isSharePointListAttachment = controlType.toLowerCase() === ATTACHMENT_TYPE_SHAREPOINT;

    const isReadOnly = disableEditing && !isSharePointListAttachment;

    // String
    // In list forms, the attachment control is always kept empty and is bound to a group with child elements for files that have already been attached, 
    // so we want to ignore its node value
    const currentValue = isSharePointListAttachment ? '' : field && field.value();

    // FileProperties
    const fileProperties = currentValue && getFileProperties(field.getNode());

    //Returns true if fileName is the same as an existing row - case insensitive
    function isDuplicateName(fileName) {
        const attachments = field.selectNodes("*");
        const fileNameLower = fileName.toLowerCase();

        return attachments.some((dsNode) =>
            getNodeData(dsNode.getNode(), FILE_ATTACH_URL_KEY).toLowerCase() === fileNameLower
        );
    }

    function handleListAttachment(data) {
        const fieldNode = field.getNode();
        const doc = ownerDocument(fieldNode);
        const attachmentUrl = doc.createElement('attachmentURL');

        const decoded = decodeBase64Attachment(data);
        const filename = decoded.name;

        //check for duplicate name
        if (isDuplicateName(filename)) {
            const errorDialog = failureDialog('Error', 'File already exists. Select a different file or rename the file.');
            return errorDialog.showAsync();
        }

        setNodeData(attachmentUrl, FILE_DATA_KEY, data);
        setNodeData(attachmentUrl, FILE_ATTACH_URL_KEY, filename);
        setNodeData(attachmentUrl, IS_LOCAL_KEY, true);

        return field.appendChildAsync(attachmentUrl);
    }

    // Sets the file in the specified dialog result to the current field
    // FileDialogResult -> Promise
    async function setFileToFieldAsync(dialogResult) {
        // .getAttachmentBase64Async() contains processing logic that can take a while
        const data = await showLoaderText(
            'Processing...',
            dialogResult.getAttachmentBase64Async(),
        );

        if (data.indexOf("WEB_WORKER_ERROR") === -1) {
            if (isSharePointListAttachment) {
                await handleListAttachment(data);
            } else {
                await clearFileAsync();
                await field.setValueAsync(data);
            }
        } else {
            const ffd = failureDialog('Error', 'Attachment upload failed, please try again.', data);

            await ffd.showAsync();

            await field.setValueAsync('');
        }
    }

    // Handles the result of a file selection dialog
    // FileDialogResult -> Promise
    async function handleFileSelectionResultAsync(result) {
        switch (result.result) {
            case DIALOG_RESULT_RETRIEVED:
                await setFileToFieldAsync(result);
                return;
            case DIALOG_RESULT_CANCEL:
                // do nothing
                return;
            default:
                console.warn(`Unknown file selection result: ${result.result}`);
                return;
        }
    }

    // Initiates file selection and returns a promise that resolves when the dialog and ensuing
    // processes are complete
    // () -> Promise
    const doFileSelectionAsync = async () => {
        const dialog = fileDialog({ preprocessBase64: true });

        const result = await dialog.showAsync();

        await handleFileSelectionResultAsync(result);
    }

    // Initiates a download of the attachment's file to the user's browser
    // () -> void
    function initFileDownload() {
        const file = decodeBase64Attachment(currentValue);

        initiateFileDownload(file.bytes, file.name);
    }

    // Clears the value of the file
    // () -> Promise
    const clearFileAsync = async () => {
        // TODO: set to nil if nillable
        om.setNodeValueAsync(field.getNode(), "");

        await field.setValueAsync('');
    }

    // Initiates an action based on the option the user selected
    // () -> Promise
    const handleFileActionResultAsync = async (result) => {
        switch (result) {
            case ACTION_DOWNLOAD:
                initFileDownload();
                return;

            case ACTION_REMOVE:
                await clearFileAsync();
                return;

            case ACTION_REPLACE:
                await doFileSelectionAsync();
                return;

            case ACTION_CANCEL:
                // do nothing
                return;
        }
    }

    // Displays a dialog for the actions on an attachment and executes the selected action
    // () -> Promise
    async function doFileActionSelectionAsync() {
        const result = await showFileActionDialog((fileProperties && fileProperties.name) || "");

        await handleFileActionResultAsync(result);
    }

    const handleReadOnlyClick = () => {
        if (currentValue) {
            initFileDownload();
        }
    };

    const handleEditableClick = async () => {
        if (currentValue) {
            await doFileActionSelectionAsync();
        } else {
            await doFileSelectionAsync();
        }
    };

    // Handles a click on an attachment control:
    // - If the control has a file attached, shows a dialog with actions and executes the selected action
    // - If not, prompts the user to attach a file
    // - If there is no actual associated field, does nothing
    // () -> Promise
    async function handleClickAsync() {
        if (!field) {
            return;
        }

        if (isReadOnly) {
            handleReadOnlyClick();
        } else {
            await handleEditableClick();
        }
    }

    return {
        handleClickAsync,
    };
}

export default attachmentClickHandler;
